import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { CardContainer, ResponseRateText, RectangleText, TitleText, getAttributeBasedTitle } from './_shared';
import {
  DEFAULT_RESULTS_TRANSLATION,
  COLORS,
  NPS,
  YES_NO_ICON_PATH,
  NPS_PROMOTERS_INDEX,
  NPS_DETRACTORS_INDEX,
} from '../../constants/results';

const useStyles = makeStyles(() => ({
  text: {
    color: '#4f4f4f',
    fontSize: 13,
  },
  bottomText: {
    color: '#4f4f4f',
    fontSize: 13,
    marginBottom: 8,
  },
  box: {
    border: `2px solid ${COLORS.MAIN}`,
    borderRadius: 8,
    width: 25,
    height: 25,
    display: 'flex',
    justifyContent: 'center',
    color: COLORS.MAIN,
  },
  option: {
    color: '#0F7B85',
  },
  s3Icons: {
    width: 25,
    height: 25,
  },
  divider: {
    borderLeft: '1px solid #E0E0E0',
    height: 50,
  },
  rectMain: {
    fontWeight: 500,
    fontSize: 12,
    textAlign: 'center',
    color: '#4F4F4F',
    margin: '0 0 5px 0',
  },
  rectNumber: {
    fontWeight: 'bold',
    fontSize: 18,
    textAlign: 'center',
    color: '#333333',
    margin: 0,
  },
  loadingPage: {
    position: 'relative',
    height: '55vh',
  },
  ratingNumOfResponsesText: {
    display: 'flex',
    paddingLeft: 4,
    alignItem: 'center',
    color: (props) => (props.percent >= 10 ? COLORS.WHITE : COLORS.BLACK),
  },
  numbers: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    width: '100%',
    paddingTop: 16,
    borderTop: '1px solid #E0E0E0',
  },
  comment: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 14,
    color: '#14A5B2',
    border: '1px solid #14A5B2',
    borderRadius: 4,
    marginTop: 16,
    display: 'flex',
    justifyContent: 'center',
    padding: '4px 0',
  },
}));

const formatAverage = (average) => {
  return average ? (average * 100).toFixed(1) : 0;
};

const RatingIcon = ({ index, ratingType, ratingIcon }) => {
  const classes = useStyles();

  if (ratingType && ratingIcon) {
    return (
      <img
        className={classes.s3Icons}
        src={`https://disengage-icons.s3-us-west-2.amazonaws.com/${ratingType}/${ratingIcon}/${index}.png`}
        alt="rating-icon"
        aria-hidden
      />
    );
  }

  return <Typography className={classes.box}>{index}</Typography>;
};

const getMissingRatingData = (ratings, minRating, maxRating) => {
  const transformedRatings = [];
  for (let i = minRating; i <= maxRating; i++) {
    const targetRating = ratings.find((rating) => rating.rating === i);
    transformedRatings.push(
      targetRating || {
        rating: i,
        totalAnswered: 0,
        numberOfComments: 0,
        percentage: 0,
      },
    );
  }
  return transformedRatings;
};

export default function ScaleCard({
  title,
  leftLabel,
  rightLabel,
  isAttributeBased,
  questionAttributes,
  canViewComments,
  attributes = [],
  shouldShowLabels,
  questionType,
  result,
  handleCardClick,
  handleCommentClick,
  showHoverover = true,
  translation = DEFAULT_RESULTS_TRANSLATION,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const { totalAnswered, totalRespondents, numberOfComments, ratings, average, iconPath, minRating, maxRating } =
    result;

  const iconType = iconPath.split('_')[0];
  const ratingIcon = iconPath.includes(YES_NO_ICON_PATH) ? iconPath : `ratings_${iconPath}`;
  const ratingType = iconPath.includes(YES_NO_ICON_PATH) ? YES_NO_ICON_PATH : `ratings_${iconType}`;

  const completeRatings = getMissingRatingData(ratings, minRating, maxRating);

  const renderNpsState = () => {
    let likeTotal = 0;
    let dislikeTotal = 0;
    completeRatings.forEach((completeRating) => {
      if (NPS_PROMOTERS_INDEX.includes(completeRating.rating)) {
        likeTotal += completeRating.totalAnswered;
      } else if (NPS_DETRACTORS_INDEX.includes(completeRating.rating)) {
        dislikeTotal += completeRating.totalAnswered;
      }
    });
    const enpsScore = ((likeTotal - dislikeTotal) / totalAnswered) * 100;
    return (
      <>
        <div className={classes.divider} />
        <Typography className={classes.rectAvgContainer}>
          <p className={classes.rectMain}>{translation.eNPS}</p>
          <p className={classes.rectNumber}>{enpsScore.toFixed(0)}</p>
        </Typography>
      </>
    );
  };

  const renderRatingStat = () => {
    const formattedAverage = formatAverage(average);
    const positiveStart = Math.ceil(maxRating / 2) + 1;
    let positiveTotal = 0;
    for (let i = positiveStart; i <= maxRating; i++) {
      const ratingDetail = completeRatings.find((detail) => detail.rating === i);
      positiveTotal += ratingDetail.totalAnswered;
    }
    const formattedPositive = formatAverage(positiveTotal / totalAnswered);

    return (
      <>
        <div className={classes.divider} />
        <div className={classes.rectAvgContainer}>
          <p className={classes.rectMain}>{translation.avg}</p>
          <p className={classes.rectNumber}>{formattedAverage}%</p>
        </div>
        <div className={classes.divider} />
        <div className={classes.rectAvgContainer}>
          <p className={classes.rectMain}>{translation.positive}</p>
          <p className={classes.rectNumber}>{formattedPositive}%</p>
        </div>
      </>
    );
  };

  const attributesTitle = getAttributeBasedTitle(isAttributeBased, questionAttributes, attributes);

  const renderHeader = () => (
    <Grid item xs={12}>
      <TitleText
        translation={translation}
        title={title}
        isAttributeBased={isAttributeBased}
        attributesTitle={attributesTitle}
      />
      <Typography className={classes.text}>{shouldShowLabels && leftLabel}</Typography>
      <Grid container>
        {completeRatings.map((data, index) => {
          const percentage = (data.percentage * 100).toFixed(1);
          const numberOfResponses = focused && showHoverover ? data.totalAnswered : null;
          return (
            <Grid item key={index} xs={12} style={{ padding: '6px 0px' }}>
              <Grid container>
                <Grid item xs={2}>
                  <RatingIcon ratingIcon={ratingIcon} ratingType={ratingType} index={data.rating} />
                </Grid>

                <Grid item xs={1} tabIndex={0}>
                  <span className={classes.option} aria-label={t('screenReader.option', { option: data.rating })}>
                    {data.rating}
                  </span>
                </Grid>

                <Grid item xs={6}>
                  <RectangleText percent={percentage} numberOfResponses={numberOfResponses} />
                </Grid>

                <Grid item xs={3} container justifyContent="center" alignContent="center" tabIndex={0}>
                  <Typography aria-label={t('screenReader.peopleSelected', { percent: percentage })}>
                    {`${percentage}%`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Typography className={classes.bottomText}>{shouldShowLabels && rightLabel}</Typography>
    </Grid>
  );

  return (
    <CardContainer
      renderHeader={renderHeader}
      translation={translation}
      onClick={handleCardClick && handleCardClick(result)}
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
    >
      {(totalAnswered || totalRespondents) && (
        <Grid className={classes.numbers}>
          <ResponseRateText
            translation={translation}
            totalAnswered={totalAnswered}
            totalRespondents={totalRespondents}
            attributesTitle={attributesTitle}
          />
          {questionType === NPS ? renderNpsState() : renderRatingStat()}
        </Grid>
      )}

      {canViewComments && numberOfComments > 0 && (
        <Grid container className={classes.comment} onClick={handleCommentClick && handleCommentClick(result)}>
          {`${translation.see} ${numberOfComments} ${translation.comments}`}
        </Grid>
      )}
    </CardContainer>
  );
}
